import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { FormModal, Field, FilePicker, inputCls, btnPrimary, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ReviewerSelect, { useClearOffRosterPick, MASTER_REVIEWER_HINT } from '../components/workflow/ReviewerSelect';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import {
  ClientFormFields, clientFormFromRequest, clientRequiredGaps, clientFormData,
  useRowLetters, useCompanyChecks, hasCompanyBlockers, rowFieldKey,
  APPOINTMENT_DOC_TYPE, STRUCTURE_DOC_TYPE,
} from './WorkflowClientNew';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import NowName from '../components/shared/NowName';
import RefLink from '../components/shared/RefLink';
import { EntityCategoryChip } from '../components/shared/entityCategory';

// New Client registration — detail page (docs/modules/workflow-client.md).
// Requester modifies (until approved); the reviewer named on the request
// approves / returns it.
// Approval writes the group and every company into the client master in one
// transaction (+ lands the Appointment Letters and appends any group
// structure diagram to the group's history).
const isStructure = (f) => f.docType === STRUCTURE_DOC_TYPE;
const clientFileBadge = (f, entities = []) => {
  if (f.docType === STRUCTURE_DOC_TYPE) return ['Group structure', 'bg-purple-50 text-purple-700'];
  if (f.docType === APPOINTMENT_DOC_TYPE) {
    const owner = f.entityRowId ? (entities || []).find((e) => e.id === f.entityRowId) : null;
    return [owner ? `Appointment letter — ${owner.entityName}` : 'Appointment letter (shared)', 'bg-emerald-50 text-emerald-700'];
  }
  return ['Supporting', 'bg-slate-100 text-slate-600'];
};

const LETTER_MODE_LABEL = { shared: "This request's letter", own: 'Its own letter', existing: 'Existing group letter' };

// The keys the backend diffs on modify (wfClientRepository DIFF_FIELDS).
const CHANGE_LABELS = {
  entities: 'Companies', clientGroup: 'Client Group', remarks: 'Remarks',
};

const WorkflowClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [review, setReview] = useState(null);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/client/${id}`),
        fetchJSON(`/api/workflow/client/${id}/events`),
      ]);
      setRequest(detail.request);
      setCanReview(detail.canReview);
      setReview(detail.review || null);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/client/${id}/approve`, { method: 'POST' });
      setNotice('Approved — the companies are now in the Clients & Entities master and the requester has been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/client/${id}/resubmit`, { method: 'POST' });
      setNotice('Re-submitted — the reviewer has been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';
  const entities = request?.entities || [];
  const supportingFiles = (request?.files || []).filter((f) => !isStructure(f));
  const structureFiles = (request?.files || []).filter(isStructure);
  const letterLabel = (e) => {
    if (e.letterMode === 'own') {
      const own = (request.files || []).find((f) => f.docType === APPOINTMENT_DOC_TYPE && f.entityRowId === e.id);
      return own ? `Its own letter — ${own.originalName}` : 'Its own letter (missing)';
    }
    return LETTER_MODE_LABEL[e.letterMode] || e.letterMode;
  };

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (!!review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: CHANGE_LABELS,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/client"
      backLabel="New Client"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="New Client"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          <StatusActionsCard
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested' ? approve : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            {request.status === 'approved' && (
              <div>
                <p className={detailLabel}>Master</p>
                <p className="text-sm text-green-600 mt-1">
                  {entities.length === 1 ? 'Registered in Clients & Entities' : `${entities.length} companies registered in Clients & Entities`}
                </p>
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={CHANGE_LABELS}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          <SectionCard title="Client Group">
            <DetailGrid>
              <DetailItem label="Client Group" field="clientGroup">
                <NowName value={request.clientGroup} now={request.clientGroupNow} />
                {request.isNewGroup && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">NEW GROUP</span>}
              </DetailItem>
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          <SectionCard title={`Companies (${entities.length})`}>
            <div className="table-scroll">
              <table className="min-w-full text-sm">
                <thead className="bg-white">
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase border-b border-slate-200">
                    <th className="py-2 pr-4">#</th>
                    <th className="py-2 pr-4">Entity Name</th>
                    <th className="py-2 pr-4">Entity Category</th>
                    <th className="py-2 pr-4">UEN</th>
                    <th className="py-2 pr-4">Date of Appointment</th>
                    <th className="py-2 pr-4">Appointment Letter</th>
                    {request.status === 'approved' && <th className="py-2 pr-4">Master</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {entities.map((e, i) => (
                    <tr key={e.id} className="align-top">
                      <td className="py-2 pr-4 text-slate-500">{i + 1}</td>
                      <td className="py-2 pr-4 text-slate-900">
                        <NowName value={e.entityName} now={e.entityNameNow} />
                        <FieldCommentAnchor field={rowFieldKey(e.id)} label={`Company ${i + 1}: ${e.entityName}`} compact />
                      </td>
                      <td className="py-2 pr-4">
                        {e.entityCategory
                          ? <EntityCategoryChip value={e.entityCategory} />
                          : <span className="text-amber-700">Not answered — filed before the field existed; the requester adds it on Modify.</span>}
                      </td>
                      <td className="py-2 pr-4 text-slate-700"><NowName value={e.uen} now={e.uenNow} /></td>
                      <td className="py-2 pr-4 text-slate-700 whitespace-nowrap">
                        {e.dateOfAppointment
                          ? formatDate(e.dateOfAppointment)
                          : <span className="text-amber-700">Not answered — filed before the field existed; the requester adds it on Modify.</span>}
                      </td>
                      <td className="py-2 pr-4 text-slate-700">{letterLabel(e)}</td>
                      {request.status === 'approved' && (
                        <td className="py-2 pr-4">
                          {e.entityId
                            ? <RefLink to={`/clients/${e.entityId}`} className="text-blue-600 hover:text-blue-800">Registered</RefLink>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <FieldCommentAnchor field="entities" label="Companies" />
          </SectionCard>

          {structureFiles.length > 0 && (
            <SectionCard title="Group Structure Diagram">
              <AttachmentsList
                files={structureFiles}
                downloadHref={(f) => `${API_BASE_URL}/api/workflow/client/${request.id}/files/${f.id}`}
              />
              <FieldCommentAnchor field="files" label="Attachments" />
              <p className="text-xs text-slate-500 mt-2">On approval this is added to the client group's structure history.</p>
            </SectionCard>
          )}

          <SectionCard title="Appointment Letters & Supporting Files">
            <AttachmentsList
              files={supportingFiles}
              badgeFor={(f) => clientFileBadge(f, entities)}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/client/${request.id}/files/${f.id}`}
            />
            {request.appointmentLetterId && (
              <p className="text-xs text-slate-500 mt-2">The shared Appointment Letter is an existing group letter (#{request.appointmentLetterId}) — no new upload on this request.</p>
            )}
          </SectionCard>

          <HistoryCard events={events} changeLabels={CHANGE_LABELS} />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/client/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <ClientModifyModal
              request={request}
              events={events}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the request back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its files. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/client/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/client');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Modify = the New page's exported field blocks, prefilled from the request,
// measured by the same required-field ruler and posted through the same
// FormData builder. Only what Modify alone has stays here: the removable
// current attachments and the extra supporting-files picker.
const ClientModifyModal = ({ request, events = [], onClose, onDone }) => {
  const [groups, setGroups] = useState([]);
  const [f, setF] = useState(() => clientFormFromRequest(request));
  const [structureFile, setStructureFile] = useState(null);
  const [appointmentFile, setAppointmentFile] = useState(null);
  const [rowLetters, setRowLetter] = useRowLetters();
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo, kept } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const setReviewer = useCallback((v) => setF((prev) => ({ ...prev, reviewerStaffId: v })), []);
  // A named reviewer no longer on the roster is cleared and must be re-picked.
  useClearOffRosterPick(f.reviewerStaffId, setReviewer, null, 'master');

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/client/options');
        setGroups(data.groups || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Same ruler as the New page; a company's own letter that survives this save
  // also satisfies that row's mandatory Appointment Letter.
  const keptFiles = kept(request.files || []);
  const keptOwnRowIds = keptFiles.filter((x) => x.docType === APPOINTMENT_DOC_TYPE && x.entityRowId).map((x) => x.entityRowId);
  const gaps = useMemo(
    () => clientRequiredGaps(f, { rowLetters, keptOwnRowIds }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [f, rowLetters, keptOwnRowIds.join(',')]
  );
  const validation = useFormValidation(gaps);
  const checks = useCompanyChecks(f.entities, request.id);
  const blocked = hasCompanyBlockers(checks);

  // Own letters bound to rows: the picker replaces them, so no Remove button
  // (removing one only leads to a refused save).
  const removableFiles = (request.files || []).filter((x) => !(x.docType === APPOINTMENT_DOC_TYPE && x.entityRowId));

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (blocked) { setError('A company on this request is already registered or under review — see the note under that company.'); return; }
    setSaving(true);
    setError(null);
    try {
      const form = clientFormData(f, { structureFile, appointmentFile, rowLetters, files });
      appendTo(form);
      await fetchJSON(`/api/workflow/client/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || blocked} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <ClientFormFields
          f={f}
          set={set}
          groups={groups}
          existingFiles={keptFiles}
          structureFile={structureFile}
          setStructureFile={setStructureFile}
          appointmentFile={appointmentFile}
          setAppointmentFile={setAppointmentFile}
          rowLetters={rowLetters}
          setRowLetter={setRowLetter}
          checks={checks}
        />
        <EditableAttachments
          files={removableFiles}
          removeIds={removeIds}
          onToggle={toggle}
          downloadHref={(x) => `${API_BASE_URL}/api/workflow/client/${request.id}/files/${x.id}`}
          badgeFor={(x) => clientFileBadge(x, request.entities)}
        />
        <FieldCommentInline field="files" />
        <Field label="Add More Supporting Files">
          <FilePicker
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </Field>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={f.remarks} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Reviewer" field="reviewerName" required hint={MASTER_REVIEWER_HINT}>
          <ReviewerSelect roster="master" value={f.reviewerStaffId} onChange={setReviewer} />
        </Field>
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowClientDetail;
